import { useEffect, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { useNavigate } from "react-router-dom";
import { ArrowRight, ChevronRight, FastForward } from "lucide-react";
import { IMAGES } from "../lib/tokens";
import { useWorld } from "../state/World";
import WalkArrow from "./WalkArrow";

/**
 * The walk-in engine. One implementation, every Place.
 *
 * Click-driven, never auto-played unless the visitor asks: on stage a judge has
 * to be able to steer. Every beat is one entry in lib/beats.js — adding or
 * reordering a Place's walk is an edit there and nothing else.
 */
export const WalkIn = ({
  beats,
  realm,
  pathPrefix,
  arriveIndex,
  arriveTo,
  arriveLabel,
  skipLabel,
  placeName,
  choiceLine,
}) => {
  const { me, mark } = useWorld();
  const nav = useNavigate();
  const [i, setI] = useState(0);
  const [auto, setAuto] = useState(null); // null = not chosen yet, true = walk me

  const beat = beats[i];
  const plate = IMAGES[beat.image] || IMAGES[beats[0].image];
  const last = i >= beats.length - 1;
  const arrived = i >= arriveIndex;

  // Resume where they stopped walking. Engagements ride along on /me.
  useEffect(() => {
    const at = me?.engagements?.find((e) => e.realm === realm)?.last_state || "";
    const m = at.match(new RegExp(`^${pathPrefix}/(\\w+)$`));
    if (!m) return;
    const found = beats.findIndex((b) => b.key === m[1]);
    if (found > 0) setI(found);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    mark(realm, `${pathPrefix}/${beat.key}`);
  }, [beat.key, mark, realm, pathPrefix]);

  // Auto-walk stops at the threshold — arriving is a decision, never something
  // that happens to you while you are watching.
  useEffect(() => {
    if (!auto || arrived) return;
    const t = setTimeout(() => setI((v) => Math.min(v + 1, arriveIndex)), 5200);
    return () => clearTimeout(t);
  }, [auto, i, arrived, arriveIndex]);

  const advance = () => {
    if (arrived) return nav(arriveTo);
    setI((v) => Math.min(v + 1, beats.length - 1));
  };

  useEffect(() => {
    const key = (e) => {
      if (["Enter", " ", "ArrowRight"].includes(e.key)) {
        e.preventDefault();
        advance();
      }
      if (e.key === "ArrowLeft") setI((v) => Math.max(0, v - 1));
    };
    window.addEventListener("keydown", key);
    return () => window.removeEventListener("keydown", key);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [i, arrived]);

  return (
    <div
      className="relative min-h-screen cursor-pointer overflow-hidden bg-[#05060a]"
      data-testid={`beat-${beat.key}`}
      onClick={advance}
    >
      <AnimatePresence mode="wait">
        <motion.img
          key={beat.key}
          src={plate}
          alt=""
          className="absolute inset-0 h-full w-full object-cover"
          initial={{ opacity: 0, scale: 1.06 }}
          animate={{ opacity: 1, scale: 1 }}
          exit={{ opacity: 0, scale: 1.02 }}
          transition={{ duration: 1.1, ease: [0.2, 0.8, 0.2, 1] }}
        />
      </AnimatePresence>

      {/* Copy sits bottom-left on the quiet part of the frame. No scrim.
          Lifted clear of the HUD pills, which live at bottom-4/6 and stand 40px
          tall — at md:p-14 the line landed underneath them and the first thing a
          visitor saw on a Practice domain was two bits of text on top of each
          other. */}
      <div className="pointer-events-none absolute inset-x-0 bottom-0 z-20 px-8 pb-24 pt-8 md:px-14 md:pb-28 md:pt-14">
        <AnimatePresence mode="wait">
          <motion.div
            key={beat.key}
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -8 }}
            transition={{ duration: 0.7, delay: 0.25 }}
            className="max-w-lg"
          >
            <div
              className="tracked mb-2.5 text-[9px] text-[var(--stage-gold)]"
              style={{ textShadow: "0 1px 8px rgba(0,0,0,0.95)" }}
            >
              {beat.kicker}
            </div>
            <p
              data-testid="beat-line"
              className="serif text-2xl font-light leading-snug text-[#f4ece0] sm:text-3xl"
              style={{
                // No scrim — founder's rule, the plate stays whole. But some
                // plates are bright exactly where the copy lands (the Arcade's
                // clouds and water), so the line carries its own shadow: one
                // tight for edge definition, one wide for separation.
                textShadow:
                  "0 1px 3px rgba(0,0,0,0.95), 0 2px 10px rgba(0,0,0,0.9), 0 4px 30px rgba(0,0,0,0.8)",
              }}
            >
              {beat.line}
            </p>
          </motion.div>
        </AnimatePresence>
      </div>

      {/* progress through the walk */}
      <div className="absolute left-8 top-8 z-20 flex gap-1.5 md:left-14 md:top-14">
        {beats.map((b, n) => (
          <span
            key={b.key}
            className="h-[2px] w-6 rounded-full transition-colors duration-500"
            style={{
              background: n <= i ? "var(--stage-gold)" : "rgba(255,255,255,0.2)",
            }}
          />
        ))}
      </div>

      {/* Direction of travel, in the plate. The button below is the fallback. */}
      <WalkArrow nav={beat.nav} onClick={advance} />

      <div className="absolute bottom-24 right-8 z-20 flex items-center gap-3 md:bottom-28 md:right-14">
        {i < arriveIndex && (
          <button
            type="button"
            data-testid="skip-walk"
            onClick={(e) => {
              e.stopPropagation();
              setI(arriveIndex);
            }}
            className="tracked flex items-center gap-1.5 rounded-full border border-white/15 px-4 py-2.5 text-[9px] text-white/45 transition-colors hover:border-white/40 hover:text-white/80"
          >
            <FastForward size={11} strokeWidth={1.8} /> {skipLabel}
          </button>
        )}
        <button
          type="button"
          data-testid="beat-advance"
          onClick={(e) => {
            e.stopPropagation();
            advance();
          }}
          className="tracked group flex items-center gap-2 rounded-full border border-[var(--stage-gold)]/40 px-5 py-2.5 text-[9px] text-[var(--stage-gold)]/80 transition-colors duration-200 hover:border-[var(--stage-gold)] hover:text-[var(--stage-gold)]"
        >
          {arrived ? arriveLabel : last ? "Go on" : "Walk on"}
          {arrived ? (
            <ArrowRight size={12} strokeWidth={2} className="transition-transform group-hover:translate-x-0.5" />
          ) : (
            <ChevronRight size={12} strokeWidth={2} className="transition-transform group-hover:translate-x-0.5" />
          )}
        </button>
      </div>

      {beat.lowres && (
        <div className="absolute right-8 top-8 z-20 md:right-14 md:top-14">
          <span className="mono rounded-full border border-white/15 px-3 py-1.5 text-[8px] text-white/35">
            low-res slice
          </span>
        </div>
      )}

      {/* The choice, offered once, at the door. */}
      <AnimatePresence>
        {auto === null && i === 0 && (
          <motion.div
            data-testid="walk-choice"
            className="absolute inset-0 z-30 grid place-items-center"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.5, delay: 0.9 }}
            onClick={(e) => e.stopPropagation()}
          >
            <div className="glass mx-6 max-w-sm rounded-3xl p-7 text-center">
              <div className="tracked mb-2 text-[9px] text-[var(--stage-gold)]">
                {placeName}
              </div>
              <p className="serif mb-7 text-xl leading-snug text-white/90">{choiceLine}</p>
              <div className="flex flex-col gap-2.5">
                <button
                  type="button"
                  data-testid="walk-click"
                  onClick={() => setAuto(false)}
                  className="tracked rounded-full bg-[var(--stage-gold)] px-6 py-3.5 text-[10px] uppercase text-[#1a1206] transition-all hover:brightness-110"
                >
                  I will click through
                </button>
                <button
                  type="button"
                  data-testid="walk-auto"
                  onClick={() => setAuto(true)}
                  className="tracked rounded-full border border-white/20 px-6 py-3.5 text-[10px] uppercase text-white/65 transition-colors hover:border-[var(--stage-gold)]/60 hover:text-white/90"
                >
                  Walk me through it
                </button>
              </div>
              <p className="mono mt-5 text-[9px] leading-relaxed text-white/30">
                Either way you can stop, step back, or skip ahead.
              </p>
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      {auto && !arrived && (
        <button
          type="button"
          data-testid="walk-take-over"
          onClick={(e) => {
            e.stopPropagation();
            setAuto(false);
          }}
          className="tracked absolute bottom-8 left-1/2 z-20 -translate-x-1/2 text-[9px] text-white/35 transition-colors hover:text-white/75 md:bottom-14"
        >
          Let me take it from here
        </button>
      )}

      {!me && (
        <button
          type="button"
          data-testid="beat-exit"
          onClick={(e) => {
            e.stopPropagation();
            nav("/");
          }}
          className="tracked absolute left-8 bottom-8 z-20 text-[9px] text-white/35 hover:text-white/70 md:left-14 md:bottom-14"
        >
          Back out
        </button>
      )}
    </div>
  );
};

export default WalkIn;
